"use client";

import { useState, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { cn } from "@/lib/utils";
import GlobeComponent, { CITY_DATA } from "@/components/sections/shared/globe";
import {
  COUNTRY_STATS,
  DEFAULT_STATS,
  topCountries,
  containerVariants,
  itemVariants,
} from "./data";
import { CountryDropdown, StatValue } from "./parts";
import { useUptime } from "@/hooks/use-uptime";
import SectionHeader from "@/components/ui/section-header";

export default function GlobalNetworkSection() {
  const prefersReducedMotion = useReducedMotion();
  const [selectedCityIdx, setSelectedCityIdx] = useState(0);
  const uptime = useUptime();

  const handleSelect = useCallback((idx: number) => {
    setSelectedCityIdx(idx);
  }, []);

  const selectedCity = selectedCityIdx >= 0 ? CITY_DATA[selectedCityIdx] : null;

  const activeStats = selectedCity
    ? (() => {
        const cs = COUNTRY_STATS[selectedCity.name];
        if (!cs) return DEFAULT_STATS;
        return [
          { value: cs.ips, label: "IPs in " + selectedCity.name },
          { value: cs.cities, label: "Cities covered" },
          { value: `${uptime.toFixed(3)}%`, label: "Uptime guarantee" },
          { value: cs.latency, label: "Avg latency" },
        ];
      })()
    : DEFAULT_STATS.map((s) =>
        s.label === "Uptime guarantee" ? { ...s, value: `${uptime.toFixed(3)}%` } : s
      );

  const statsKey = selectedCity?.name ?? "global";

  return (
    <section
      /* full-bleed: the shell constrains sections to max-w-7xl, so without it
         the band would stop at the two frame hairlines instead of running the
         full viewport width. The inner max-w-7xl wrapper keeps the content
         aligned with every other section. */
      className="full-bleed py-section border-border bg-band relative isolate overflow-hidden border-b"
      aria-labelledby="network-heading"
    >
      {/* Ambient backdrop: a single brand glow sitting under the globe.
          Deliberately CSS only: this section already runs the interactive globe
          canvas, and the homepage's WebGL contexts (hero + the two card grids)
          are close to the browser's per-page cap, so a shader here would risk
          evicting one of those.

          A negative z-index inside the section's own stacking context keeps it
          under every child without needing a z-index on each one; a positioned
          layer at z-auto would otherwise paint over the in-flow content below.
          Set inline because Tailwind v4 emits no negative z-index utility.

          The glow tokens are raw rgb triplets so alpha can be dialled per use,
          and both darken on the light theme.

          Same balance as the card washes: the blue glow leads under the globe,
          and the orange is a much fainter accent from the opposite corner —
          low enough that it warms the band rather than colouring it. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          zIndex: -1,
          background: [
            "radial-gradient(760px circle at 72% 45%, rgba(var(--glow-green), 0.13), rgba(var(--glow-green), 0.05) 40%, transparent 72%)",
            "radial-gradient(520px circle at 6% 88%, rgba(var(--glow-yellow), 0.055), rgba(var(--glow-yellow), 0.02) 42%, transparent 74%)",
          ].join(", "),
        }}
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 grid items-start gap-8 lg:grid-cols-2 lg:gap-x-16">
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.55 }}
            className="relative z-20 min-w-0"
          >
            <SectionHeader
              id="network-heading"
              eyebrow="Global Network"
              heading="Global scale. Local precision."
              body="Our network spans 195+ countries, with city-level targeting across every major market."
              bodyClassName="mb-5"
            />

            <div>
              <CountryDropdown selectedIdx={selectedCityIdx} onSelect={handleSelect} />

              <div className="mt-2 h-4">
                <AnimatePresence>
                  {selectedCity && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="text-muted-foreground flex items-center gap-1.5 text-xs"
                    >
                      Showing stats for
                      <span className="text-foreground font-semibold">
                        {selectedCity.name}
                      </span>·{" "}
                      <span className="text-brand-yellow light:text-blue-600">
                        {selectedCity.type}
                      </span>
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4">
              {activeStats.map((s, i) => {
                const hideOnMobile = i === 1 || i === 3;
                return (
                  <div
                    key={s.label}
                    className={cn(
                      "bg-card border-border overflow-hidden rounded-xl border p-4",
                      hideOnMobile && "hidden lg:block"
                    )}
                  >
                    <StatValue value={s.value} statsKey={statsKey} />
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={statsKey + s.label}
                        initial={prefersReducedMotion ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="text-muted-foreground text-sm"
                      >
                        {s.label}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.55 }}
            className="3xl:max-w-[42rem] 3xl:mx-auto relative z-10 hidden w-full min-w-0 lg:block"
            aria-label="Interactive 3D world map showing proxy network coverage"
          >
            <GlobeComponent selectedCityIdx={selectedCityIdx} />
          </motion.div>
        </div>

        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="hidden gap-3 lg:grid lg:grid-cols-8"
          aria-label="Top proxy countries"
        >
          {topCountries.map((c) => {
            const cityIdx = CITY_DATA.findIndex((cd) => cd.name === c.name);
            const isSelected = cityIdx >= 0 && cityIdx === selectedCityIdx;
            return (
              <button
                key={c.name}
                type="button"
                onClick={() => cityIdx >= 0 && handleSelect(isSelected ? -1 : cityIdx)}
                aria-pressed={isSelected}
                className={cn(
                  // each chip is its own card now, so the first/last-only radius
                  // the segmented bar needed is gone
                  "border-border flex flex-col items-center rounded-xl border p-3 text-center transition-all duration-200",
                  isSelected ? "bg-foreground" : "bg-card hover:bg-muted/40 cursor-pointer"
                )}
              >
                {/* The tile stays opaque; only its content animates in, so the
                    hairline grid's border color never shows through. */}
                <motion.span variants={itemVariants} className="flex flex-col items-center">
                  <span className="mb-1 text-2xl" aria-hidden="true">
                    {c.flag}
                  </span>
                  <span
                    className={cn(
                      "text-xs leading-tight font-medium",
                      isSelected ? "text-background" : "text-foreground"
                    )}
                  >
                    {c.name}
                  </span>
                </motion.span>
              </button>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
